/**
 * ScrollFade — 가로 스크롤 컨테이너에 "더 있다" 힌트(페이드 + 셰브론)를 붙인다 ($j.Ui.ScrollFade).
 *
 * 왜: 정렬 탭·칩 줄이 화면보다 길면 마지막 항목이 오른쪽 끝에서 잘리는데, 시니어 사용자는 그걸 "더 있다"가 아니라
 *     "잘렸다"로 읽는다(글자크기 확대에서 특히). 페이드만으로 부족해 셰브론(›)을 같이 띄우고, 끝까지 밀면 사라진다.
 *
 * 사용: <div class="u-scroll-x" data-scroll-fade>…</div>
 *       쓰는 사이트의 부트스트랩이 $j.Ui.ScrollFade.attachAll() 을 부른다 — main.js 는 전 사이트에 실리므로 스스로 붙지 않는다.
 *       JS 로 나중에 그린 컨테이너는 $j.Ui.ScrollFade.attachAll(root) 를 한 번 부른다.
 *       const ctl = $j.Ui.ScrollFade.attach(el);  // { sync, destroy }
 *
 * 스타일: shared/components/_scroll-fade.scss — 쓰는 사이트의 main.scss 가 import 한다.
 *       색·크기는 --ui-scroll-fade-{bg,ink,w,chevron,gap} 토큰으로 열려 있다(폴백이 기본 모습). 사이트 scss 가 잇는다.
 *
 * 동작
 * - 컨테이너의 overflow 는 건드리지 않는다(원래 스크롤되던 요소에만 힌트를 얹는다). 스크롤 폭이 없으면 힌트도 안 뜬다
 * - 양끝에 .u-scroll-x__hint(오른쪽) / .u-scroll-x__hint--left 를 만들어 넣고, scrollLeft 로 is-scrollable-right/left 를 토글한다
 * - flex 컨테이너는 sticky, 블록 컨테이너는 부모 기준 absolute(.u-scroll-x--block) — 자동 판별
 * - scroll · resize · ResizeObserver(컨테이너·자식 크기) · <html data-fs> 변경(글자크기 토글)에 반응한다
 * - 힌트는 aria-hidden, pointer-events:none — 탭 클릭을 가로채지 않는다
 */
export default class ScrollFade {
    attachAll(root = document) {
        const list = root.querySelectorAll ? root.querySelectorAll('[data-scroll-fade]:not([data-scroll-fade-on])') : [];
        list.forEach(el => this.attach(el));
        return list.length;
    }

    attach(target) {
        const el = typeof target === 'string' ? document.querySelector(target) : target;
        if (!el || el.hasAttribute('data-scroll-fade-on')) return null;
        el.setAttribute('data-scroll-fade-on', '1');
        // 블록 컨테이너(flex/grid 아님)는 sticky 힌트가 성립 안 한다 → 부모를 기준으로 absolute (scss .u-scroll-x--block)
        const disp = getComputedStyle(el).display;
        if (!/flex|grid/.test(disp) && el.parentElement) el.parentElement.classList.add('u-scroll-x--block');

        const mk = (cls) => {
            const h = document.createElement('span');
            h.className = 'u-scroll-x__hint' + (cls ? ' ' + cls : '');
            h.setAttribute('aria-hidden', 'true');
            return h;
        };
        const left  = el.insertBefore(mk('u-scroll-x__hint--left'), el.firstChild);
        const right = el.appendChild(mk(''));

        let raf = 0;
        const sync = () => {
            raf = 0;
            const max = el.scrollWidth - el.clientWidth;
            el.classList.toggle('is-scrollable-right', max > 1 && el.scrollLeft < max - 1);
            el.classList.toggle('is-scrollable-left',  max > 1 && el.scrollLeft > 1);
        };
        const schedule = () => { if (!raf) raf = requestAnimationFrame(sync); };

        el.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);
        let ro = null;
        if (window.ResizeObserver) { ro = new ResizeObserver(schedule); ro.observe(el); Array.from(el.children).forEach(c => ro.observe(c)); }
        const mo = new MutationObserver(schedule);                       // 글자크기 토글(data-fs) → 폭이 바뀐다
        mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-fs'] });
        const mo2 = new MutationObserver(schedule);                      // 탭이 나중에 추가/삭제되는 경우
        mo2.observe(el, { childList: true });

        sync();
        return {
            sync,
            destroy() {
                el.removeEventListener('scroll', schedule); window.removeEventListener('resize', schedule);
                if (ro) ro.disconnect(); mo.disconnect(); mo2.disconnect();
                left.remove(); right.remove();
                el.classList.remove('is-scrollable-right', 'is-scrollable-left');
                el.removeAttribute('data-scroll-fade-on');
            },
        };
    }
}
